import { createRouter, createWebHistory } from 'vue-router';
import { i18n, loadLocale } from '@shared/i18n';
import { createScrollRestoration } from '@shared/router-scroll';
import {
    registrationStepAdditionalInformation,
    registrationStepDepartments,
    registrationStepDisabled,
    registrationStepDogData,
    registrationStepDone,
    registrationStepFileUploads,
    registrationStepMemberBaseData,
    registrationStepMemberExtendedData,
    registrationStepPaymentDetails,
    registrationStepReview,
    registrationStepStart,
} from '../steps';

// Nur fuenf Locales — angular.json fuehrt fuer diese App genau diese (i18n.locales bzw. production
// `localize`), und der Sprachumschalter der Wizard-Shell ist im Template hart auf dieselben fuenf
// gesetzt (`[locales]="['de','en','es','tr','uk']"`). NICHT die elf `messages.<lang>.xlf` im
// src/locale-Ordner — das sind Extraktions-Artefakte, kein ausgeliefertes Sprachangebot.
const SUPPORTED_LOCALES = ['de', 'en', 'es', 'tr', 'uk'];

function detectLocale(): string {
    const current = i18n.global.locale.value;
    if (SUPPORTED_LOCALES.includes(current)) {
        return current;
    }
    const lang = navigator.language.slice(0, 2);
    return SUPPORTED_LOCALES.includes(lang) ? lang : 'de';
}

// Not `.kgr-pageLayout__main` — diese App hat keine pageLayout-Shell. Angulars
// `member-registration.component.ts:275` uebergibt `setScrollableElement(this.content)`, also das
// scrollende `<main>` der Wizard-Shell; dies ist dasselbe Element.
const { trackScrollPositions, scrollBehavior } = createScrollRestoration('main.kgr-scrollable');

export const router = createRouter({
    // Reine Browser-SPA: immer History, nie Hash — die App wird nicht nativ gewrappt.
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior,
    routes: [
        {
            path: '/',
            redirect: () => `/${detectLocale()}/`,
        },
        {
            // Faengt locale-lose Pfade ab. Die Locale-Gruppe ist bewusst auf die fuenf
            // unterstuetzten eingeschraenkt, sonst schluckte `:locale` auch `:clubId`.
            path: '/:pathMatch(.*)*',
            redirect: (to) => {
                const segments = Array.isArray(to.params.pathMatch) ? to.params.pathMatch.join('/') : to.params.pathMatch;
                const first = segments.split('/')[0];
                if (SUPPORTED_LOCALES.includes(first)) {
                    return { path: '/', query: to.query, hash: to.hash };
                }
                return { path: `/${detectLocale()}/${segments}`, query: to.query, hash: to.hash };
            },
        },
        {
            path: '/:locale(de|en|es|tr|uk)',
            children: [
                {
                    // Angular rendert unter `/anmeldung/` NICHTS: die einzige Top-Level-Route des
                    // Registrierungs-Moduls ist `:clubId`, und die matcht kein leeres Segment.
                    // Ohne diesen Eintrag faenge der `:pathMatch`-Catch-all unten die URL ab.
                    path: '',
                    component: () => import('../views/EmptyView.vue'),
                },

                {
                    // Angular: `member-registration-routing.module.ts` — `path: ':clubId'` mit
                    // `MemberRegistrationComponent` als Rahmen und den Steps als Kindern.
                    // `\\d+` statt freiem Platzhalter waere FALSCH: Angular matcht hier jedes
                    // Segment, parst es mit `+params['clubId']` zu `NaN` und landet ueber den
                    // fehlgeschlagenen Config-Request beim `disabled`-Screen. Gemessen am
                    // 16.09.2026 an `/anmeldung/foobar`.
                    path: ':clubId',
                    component: () => import('../views/MemberRegistrationView.vue'),
                    children: [
                        {
                            // Angular: `{ path: '', component: RegistrationStepStartComponent }` —
                            // KEIN Redirect, die URL `/anmeldung/:clubId` bleibt stehen.
                            path: '',
                            component: () => import('../views/steps/StartStepView.vue'),
                            // Angular: jede Step-Komponente deklariert `readonly registrationStep`,
                            // die Shell liest ihn beim `router-outlet`-activate. Hier haengt er an
                            // der Route — dasselbe Ergebnis, ohne Komponenten-Instanz-Zugriff.
                            meta: { step: registrationStepStart },
                        },
                        {
                            path: 'start',
                            component: () => import('../views/steps/StartStepView.vue'),
                            meta: { step: registrationStepStart },
                        },
                        {
                            path: 'disabled',
                            component: () => import('../views/steps/DisabledStepView.vue'),
                            meta: { step: registrationStepDisabled },
                        },
                        {
                            path: registrationStepMemberBaseData.path,
                            component: () => import('../views/steps/AddMembersStepView.vue'),
                            meta: { step: registrationStepMemberBaseData },
                        },
                        {
                            path: registrationStepMemberExtendedData.path,
                            component: () => import('../views/steps/MembersDataStepView.vue'),
                            meta: { step: registrationStepMemberExtendedData },
                        },
                        {
                            path: registrationStepDogData.path,
                            component: () => import('../views/steps/DogsStepView.vue'),
                            meta: { step: registrationStepDogData },
                        },
                        {
                            path: registrationStepDepartments.path,
                            component: () => import('../views/steps/DepartmentsStepView.vue'),
                            meta: { step: registrationStepDepartments },
                        },
                        {
                            path: registrationStepFileUploads.path,
                            component: () => import('../views/steps/FileUploadStepView.vue'),
                            meta: { step: registrationStepFileUploads },
                        },
                        {
                            path: registrationStepPaymentDetails.path,
                            component: () => import('../views/steps/PaymentDetailsStepView.vue'),
                            meta: { step: registrationStepPaymentDetails },
                        },
                        {
                            path: registrationStepAdditionalInformation.path,
                            component: () => import('../views/steps/AdditionalInformationStepView.vue'),
                            meta: { step: registrationStepAdditionalInformation },
                        },
                        {
                            path: registrationStepReview.path,
                            component: () => import('../views/steps/ReviewStepView.vue'),
                            meta: { step: registrationStepReview },
                        },
                        {
                            path: registrationStepDone.path,
                            component: () => import('../views/steps/DoneStepView.vue'),
                            meta: { step: registrationStepDone },
                        },
                    ],
                },

                {
                    // Angular hat KEINE `**`-Wildcard und kein ErrorRoutingModule (app.routes.ts
                    // laedt nur das Registrierungs-Modul). Eine nicht gematchte URL rendert dort
                    // eine leere Seite und BEHAELT ihren Pfad. Genau das bildet dieser Eintrag ab:
                    // ohne ihn liefe die URL in den globalen Catch-all oben, wuerde auf
                    // `/<locale>/` umgeschrieben und die Adresszeile waere eine andere als in
                    // Angular. Muss die LETZTE Route unter `/:locale` bleiben.
                    path: ':pathMatch(.*)*',
                    component: () => import('../views/EmptyView.vue'),
                },
            ],
        },
    ],
});

// Bewusst KEIN `registerErrorRouter(router)`: diese App hat in Angular keine Fehlerroute, und ein
// `redirectToError()` haette hier kein Ziel. Unregistriert ist der Aufruf ein stiller No-op — das
// entspricht Angular, wo gar nichts passiert.

router.beforeEach(async (to) => {
    const locale = to.params.locale as string | undefined;
    if (locale && SUPPORTED_LOCALES.includes(locale)) {
        await loadLocale(locale);
    }
    return true;
});

trackScrollPositions(router);
